/**
 * Theme is a class on <html>, not a media query.
 *
 * Someone who lives in this screen all day needs their choice to beat the OS,
 * so `light` and `dark` are explicit and `system` is what follows the OS.
 * The CSS variables in index.css are already keyed off `.dark`; this only
 * decides when that class is present.
 */
export type Theme = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "estelle.theme";

export function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark" || value === "system";
}

/** What the OS is asking for. */
export function systemTheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/** Which of the two is actually rendered, resolving `system`. */
export function resolveTheme(theme: Theme): "light" | "dark" {
  return theme === "system" ? systemTheme() : theme;
}

/**
 * Read the stored preference.
 *
 * Wrapped because `localStorage` throws outright in a browser configured to
 * block site data — not returning null, throwing — and a theme preference is
 * not worth a blank page.
 */
export function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function writeStoredTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // A preference that cannot be saved is still worth applying for this
    // session.
  }
}

/**
 * The page background in each theme (`--background` in index.css), which is
 * what the phone's status bar must match (#616). Kept in sync with the inline
 * script in index.html and with `theme_color` in the web manifest.
 */
export const STATUS_BAR_COLOR = { light: "#ffffff", dark: "#1a1917" } as const;

/** Put the class on <html>, or take it off, and match the status bar to it. */
export function applyTheme(theme: Theme): void {
  const resolved = resolveTheme(theme);
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", STATUS_BAR_COLOR[resolved]);
}
